import React, { useEffect, useState } from 'react';
import { Row, Col, Table, Tag, Spin, Empty, Tabs } from 'antd';
import { SunOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Clock, CheckCircle2, BellRing,
  ArrowUpRight, ArrowDownRight, ChevronRight,
  FilePlus2, Calculator, FolderOpen, ClipboardList,
  Timer, Mail, LayoutDashboard, TrendingUp,
} from 'lucide-react';
import Layout from '../../components/Layout';
import MaJournee from './MaJournee';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatDate, STATUT_MAP } from '../../variables/constants';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { libelleDelai } from '../../i18n/donnees';

/* ── KPI Card ───────────────────────────────────────────────────────────────── */
const KpiCard = ({ label, value, sub, icon: Icon, iconBg, iconColor, trend, onClick }) => (
  <div
    className="kpi-card"
    onClick={onClick}
    style={{
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.18s, box-shadow 0.18s',
    }}
    onMouseEnter={e => {
      if (onClick) e.currentTarget.style.boxShadow = '0 2px 10px rgba(16,24,40,0.08)';
    }}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
      <div style={{
        width: 44, height: 44, borderRadius: 12,
        background: iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={22} color={iconColor} strokeWidth={1.8} />
      </div>
      {trend !== undefined && (
        <span style={{
          display: 'flex', alignItems: 'center', gap: 3,
          fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 20,
          background: trend >= 0 ? '#dcfce7' : '#fee2e2',
          color: trend >= 0 ? '#166534' : '#991b1b',
        }}>
          {trend >= 0
            ? <ArrowUpRight size={13} />
            : <ArrowDownRight size={13} />
          }
          {Math.abs(trend)}%
        </span>
      )}
    </div>
    <div className="kpi-card__label" style={{ fontSize: 13, color: '#6b7280', fontWeight: 500, marginBottom: 4 }}>{label}</div>
    <div className="kpi-card__value" style={{ fontSize: 28, fontWeight: 700, color: '#111827', lineHeight: 1 }}>{value ?? '-'}</div>
    {sub && (
      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6, lineHeight: 1.5 }}>
        {sub}
      </div>
    )}
  </div>
);

/* ── Statut badge ── */
const StatutTag = ({ statut }) => {
  const s = STATUT_MAP[statut] || { label: statut, color: 'default' };
  return <Tag color={s.color} style={{ borderRadius: 20, fontSize: 12 }}>{s.label}</Tag>;
};

export default function Dashboard() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [sinistres, setSinistres] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [sResp, aResp, sAllResp] = await Promise.all([
          // skipErrorToast : un COURSIER n'a pas SINISTRE_READ et reçoit un 403 ici ; le .catch
          // juste après vide déjà la liste, un bandeau d'erreur n'apprendrait rien sur ce tableau de bord ouvert à tous.
          axiosInstance.get('/v1/sinistres?page=0&size=8', { skipErrorToast: true }).catch(() => ({ data: { data: [] } })),
          axiosInstance.get('/v1/delais/urgents').catch(() => ({ data: { data: [] } })),
          // skipErrorToast : même raison que l'appel ci-dessus, pour le calcul des stats RC sur la liste complète.
          axiosInstance.get('/v1/sinistres?page=0&size=500', { skipErrorToast: true }).catch(() => ({ data: { data: [] } })),
        ]);
        // Array.isArray, pas « || [] » : un objet est truthy et passerait a la Table, qui
        // ferait rawData.some(...) sur un objet -> crash. C'est ce qui arrivait quand
        // l'appel echouait et retombait sur un repli mal forme { data: { content: [] } }.
        const listeSinistres = sResp.data?.data;
        setSinistres(Array.isArray(listeSinistres) ? listeSinistres : []);
        setAlertes((aResp.data?.data || []).slice(0, 5));

        // Stats RC calculées depuis la liste complète (agrégat par sinistre)
        const all = sAllResp.data?.data || [];
        const STATUTS_RC = ['ATTENTE_RC', 'ATTENTE_PIECES_DE_RECLAMATION', 'MUR'];
        const enPhaseRc = all.filter(s => STATUTS_RC.includes(s.statut));
        const rcAttenteAction = enPhaseRc.filter(s =>
          s.positionRc === 'EN_ATTENTE' || s.positionRc === 'REJETEE').length;
        const rcSansReponse = enPhaseRc.filter(s => s.positionRc === 'EN_NEGOCIATION').length;
        const rcBloquees = enPhaseRc.filter(s => {
          if (s.positionRc === 'TRANCHEE') return false;
          const jours = s.dateDeclaration ? dayjs().diff(dayjs(s.dateDeclaration), 'day') : 0;
          return jours > 30;
        }).length;

        setStats({
          totalSinistres: sResp.data?.totalElements || (sResp.data?.data || []).length,
          enCours: (sResp.data?.data || []).filter(s => !['SOLDE', 'SANS_SUITE', 'PRESCRIPTION', 'CLOTURE'].includes(s.statut)).length,
          soldes: (sResp.data?.data || []).filter(s => s.statut === 'SOLDE').length,
          alertesUrgentes: aResp.data?.data?.length || 0,
          rcAttenteAction,
          rcSansReponse,
          rcBloquees,
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const columns = [
    {
      title: t('derniersSinistres.colonnes.numero'), dataIndex: 'numeroSinistreLocal', key: 'num', width: 160,
      render: (v, r) => (
        <button
          onClick={() => navigate(`/sinistres/${r.sinistreTrackingId}`)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            color: '#1a6b3c', fontWeight: 600, fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
          <FileText size={13} strokeWidth={2} />
          {v}
        </button>
      ),
    },
    {
      title: t('derniersSinistres.colonnes.type'), dataIndex: 'typeSinistre', key: 'type', width: 80,
      render: (v) => (
        <Tag color={v === 'SURVENU_TOGO' ? 'blue' : 'purple'} style={{ borderRadius: 20, fontSize: 11 }}>
          {v === 'SURVENU_TOGO' ? 'ET' : 'TE'}
        </Tag>
      ),
    },
    { title: t('derniersSinistres.colonnes.assure'), dataIndex: 'assureNomComplet', key: 'assure', ellipsis: true },
    { title: t('derniersSinistres.colonnes.paysEmetteur'), dataIndex: 'paysEmetteurLibelle', key: 'pays', width: 130, ellipsis: true },
    {
      title: t('derniersSinistres.colonnes.statut'), dataIndex: 'statut', key: 'statut', width: 160,
      render: (v) => <StatutTag statut={v} />,
    },
    {
      title: t('derniersSinistres.colonnes.declaration'), dataIndex: 'dateDeclaration', key: 'date', width: 110,
      render: (v) => (
        <span style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Clock size={11} />
          {formatDate(v)}
        </span>
      ),
    },
  ];

  /**
   * Colonnes des alertes delais.
   *
   * Le bandeau debordait, et le debordement cachait un defaut plus grave.
   * Cinq colonnes larges (150 + 110 + 110 + 100, plus le libelle du delai) dans
   * une carte au tiers de la rangee : antd, en `table-layout: auto`, ecrase la
   * seule colonne sans largeur - « Delai » - jusqu'a la faire disparaitre, et
   * tronque quand meme la derniere.
   *
   * Or c'est le libelle du delai qui distingue deux echeances portees par le
   * MEME dossier. Sans lui, deux alertes legitimes du sinistre 2026/0363/CI se
   * lisent comme une ligne dupliquee.
   *
   * Trois colonnes, donc, dont deux fluides. L'echeance passe en seconde ligne
   * sous le numero, et le niveau d'alerte colore le compte a rebours au lieu
   * d'occuper une colonne : « CRITIQUE » a cote de « -29j » en rouge disait
   * deux fois la meme chose.
   */
  const COULEUR_NIVEAU = {
    CRITIQUE: '#dc2626', URGENT: '#ea580c', ATTENTION: '#ca8a04', NORMAL: '#1a6b3c',
  };

  const alerteCols = [
    {
      title: t('alertes.colonnes.dossier'), dataIndex: 'sinistreNumeroLocal', key: 'sin', ellipsis: true,
      render: (v, r) => (
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontWeight: 600, color: '#1a6b3c', fontSize: 13,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{v}</div>
          <div style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>
            {t('alertes.echeance', { date: formatDate(r.dateEcheance) })}
          </div>
        </div>
      ),
    },
    {
      title: t('alertes.colonnes.delai'), dataIndex: 'libelleDelai', key: 'del', ellipsis: true,
      render: (v, r) => <span style={{ fontSize: 12.5, color: '#4b5563' }}>{libelleDelai(r) || '-'}</span>,
    },
    {
      title: t('alertes.colonnes.reste'), dataIndex: 'joursRestants', key: 'jr', width: 74, align: 'right',
      render: (v, r) => (
        <span
          title={r.niveauAlerte || undefined}
          style={{
            color: COULEUR_NIVEAU[r.niveauAlerte] || '#374151',
            fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap',
          }}
        >
          {t('alertes.joursRestants', { jours: v })}
        </span>
      ),
    },
  ];

  const kpis = [
    {
      label: t('kpi.totalSinistres'),
      value: stats?.totalSinistres,
      icon: FileText,
      iconBg: '#e8f5ed',
      iconColor: '#1a6b3c',
      trend: 12,
      onClick: () => navigate('/sinistres'),
    },
    {
      label: t('kpi.enCours'),
      value: stats?.enCours,
      icon: Clock,
      iconBg: '#fef3c7',
      iconColor: '#d97706',
      sub: t('kpi.dossiersActifs'),
      onClick: () => navigate('/sinistres'),
    },
    {
      label: t('kpi.soldes'),
      value: stats?.soldes,
      icon: CheckCircle2,
      iconBg: '#dcfce7',
      iconColor: '#16a34a',
      sub: t('kpi.ceMois'),
      onClick: () => navigate('/sinistres'),
    },
    {
      label: t('kpi.alertesUrgentes'),
      value: stats?.alertesUrgentes,
      icon: BellRing,
      iconBg: '#fee2e2',
      iconColor: '#dc2626',
      sub: t('kpi.delaisDepasses'),
      onClick: () => navigate('/delais'),
    },
  ];

  /* ── Raccourcis ── */
  const shortcuts = [
    { label: t('raccourcis.declarerSinistre'), icon: FilePlus2, path: '/sinistres/nouveau', bg: '#e8f5ed', color: '#1a6b3c' },
    { label: t('raccourcis.calculerOffre'), icon: Calculator, path: '/indemnisation', bg: '#fef3c7', color: '#d97706' },
    { label: t('raccourcis.commanderAttestations'), icon: FolderOpen, path: '/attestations', bg: '#dbeafe', color: '#2563eb' },
    { label: t('raccourcis.enregistrerPv'), icon: ClipboardList, path: '/pv-sinistres', bg: '#f3e8ff', color: '#7c3aed' },
    { label: t('raccourcis.voirDelais'), icon: Timer, path: '/delais', bg: '#fee2e2', color: '#dc2626' },
    { label: t('raccourcis.saisirCourrier'), icon: Mail, path: '/courriers', bg: '#ecfdf5', color: '#059669' },
  ];

  if (loading) return (
    <Layout title={t('titre')}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <Spin size="large" />
      </div>
    </Layout>
  );

  /**
   * Deux onglets, et « Ma journee » en premier.
   *
   * La vue d'ensemble repond a « comment va la plateforme » - une question de
   * pilotage, qu'on se pose de temps en temps. Un redacteur qui se connecte le matin
   * se pose l'autre : « qu'est-ce que je dois faire aujourd'hui ». C'est celle-la
   * qui merite l'ecran d'accueil ; l'autre est a un clic.
   *
   * Aucune route nouvelle : l'entree de menu, servie par /v1/navigation, et la garde
   * de chemin restent celles de /dashboard.
   */
  const vueEnsemble = (
    <>
      {/* ── En-tête ── */}
      <div className="cb-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'linear-gradient(135deg, #1a6b3c 0%, #22c55e 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(26,107,60,0.3)',
          }}>
            <LayoutDashboard size={22} color="#fff" strokeWidth={1.8} />
          </div>
          <div>
            <h1 className="cb-page-title" style={{ margin: 0 }}>{t('titre')}</h1>
          </div>
        </div>

      </div>

      {/* ── KPIs ── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
        {kpis.map((k, i) => (
          <Col xs={24} sm={12} lg={6} key={i}>
            <KpiCard {...k} />
          </Col>
        ))}
      </Row>

      {/* ── Derniers sinistres + Alertes ── */}
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}>
          <div className="cb-card" style={{ padding: 0 }}>
            <div className="cb-card-header" style={{
              padding: '20px 24px 16px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              borderBottom: '1px solid #f3f4f6',
            }}>
              <h2 className="cb-card-title" style={{
                margin: 0, display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 15, fontWeight: 600, color: '#111827',
              }}>
                <div style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: '#e8f5ed', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <FileText size={16} color="#1a6b3c" strokeWidth={1.8} />
                </div>
                {t('derniersSinistres.titre')}
              </h2>
              <button
                onClick={() => navigate('/sinistres')}
                style={{
                  background: 'none', border: 'none', color: '#1a6b3c', cursor: 'pointer',
                  fontWeight: 600, fontSize: 13, padding: 0,
                  display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                {t('derniersSinistres.voirTout')}
                <ChevronRight size={14} strokeWidth={2.5} />
              </button>
            </div>
            <Table
              className="cb-table"
              dataSource={sinistres}
              columns={columns}
              rowKey="sinistreTrackingId"
              pagination={false}
              size="small"
              style={{ marginTop: 0 }}
              locale={{ emptyText: <Empty description={t('derniersSinistres.aucun')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            />
          </div>
        </Col>

        {/* ── Alertes délais ── */}
        <Col xs={24} xl={8}>
          <div className="cb-card" style={{ padding: 0, height: '100%' }}>
            <div className="cb-card-header" style={{
              padding: '20px 24px 16px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              borderBottom: '1px solid #f3f4f6',
            }}>
              <h2 className="cb-card-title" style={{
                margin: 0, display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 15, fontWeight: 600, color: '#111827',
              }}>
                <div style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <BellRing size={16} color="#dc2626" strokeWidth={1.8} />
                </div>
                {t('alertes.titre')}
              </h2>
              <button
                onClick={() => navigate('/delais')}
                style={{
                  background: 'none', border: 'none', color: '#1a6b3c', cursor: 'pointer',
                  fontWeight: 600, fontSize: 13, padding: 0,
                  display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                {t('alertes.toutes')}
                <ChevronRight size={14} strokeWidth={2.5} />
              </button>
            </div>
            <Table
              className="cb-table"
              dataSource={alertes}
              columns={alerteCols}
              rowKey="id"
              pagination={false}
              size="small"
              locale={{ emptyText: <Empty description={t('alertes.aucune')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            />
          </div>
        </Col>
      </Row>

      {/* ── Raccourcis rapides ── */}
      <div style={{ marginTop: 24, marginBottom: 8 }}>
        <span style={{
          fontSize: 13, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase',
          letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <TrendingUp size={14} strokeWidth={2} />
          {t('raccourcis.titre')}
        </span>
      </div>
      <Row gutter={[12, 12]}>
        {shortcuts.map((r, i) => {
          const Icon = r.icon;
          return (
            <Col xs={12} sm={8} lg={4} key={i}>
              <div
                onClick={() => navigate(r.path)}
                style={{
                  background: r.bg,
                  borderRadius: 14,
                  padding: '18px 12px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  border: '1px solid rgba(0,0,0,0.05)',
                  transition: 'all 0.18s',
                  userSelect: 'none',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = `0 6px 18px ${r.bg}cc`;
                  e.currentTarget.style.borderColor = r.color + '44';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = 'rgba(0,0,0,0.05)';
                }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 10px',
                  boxShadow: `0 2px 8px ${r.bg}`,
                }}>
                  <Icon size={20} color={r.color} strokeWidth={1.8} />
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', lineHeight: 1.4 }}>
                  {r.label}
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </>
  );

  return (
    <Layout title={t('titre')}>
      <Tabs
        defaultActiveKey="ma-journee"
        items={[
          { key: 'ma-journee', label: <span><SunOutlined /> {t('onglets.maJournee')}</span>, children: <MaJournee /> },
          { key: 'ensemble', label: <span><LayoutDashboard size={14} style={{ verticalAlign: -2 }} /> {t('onglets.vueEnsemble')}</span>, children: vueEnsemble },
        ]}
      />
    </Layout>
  );
}